import { queryString } from '@toggle/helpers';
import { AuthDataPayload } from '@toggle/toggle';
import { differenceInHours } from 'date-fns';
import { usePostHog as usePostHogBase } from 'posthog-js/react';
import { useLocation, useNavigate } from 'react-router';

import { useThemeStore } from '~/components/theme-controller/stores/useThemeStore';
import { appPaths, authPaths } from '~/routes/app-paths';
import { queryKeys } from '~/routes/query-params';
import { Tracking } from '~/services/tracking';
import { usePostHog } from '~/services/tracking/posthog/usePostHog';
import { ApiFetchResponse } from '~/utils/api-fetch/apiFetch';
import { resetStores } from '~/utils/store/createStore';
import {
  getAuthToken,
  getRefreshToken,
  postLogin,
  postLogout,
  postRegister,
  postSimpleLogin,
} from '~/views/auth/services/auth-services';

import { useAuthStore } from './store/authStore';

export interface SignUpProps {
  email: string;
  password: string;
  isProfessional: boolean;
  referralCode?: string;
  promoCode?: string;
  termsAccepted?: string[];
}

interface UseAuth {
  email?: string;
  initStores: (session: AuthDataPayload) => Promise<void>;
  signUp: (user: SignUpProps) => Promise<ApiFetchResponse<AuthDataPayload>>;
  logout: (noQuery?: boolean) => Promise<void>;
  authProcessing: boolean;
  setAuthProcessing: (authProcessing: boolean) => void;
  unLockAccount: () => void;
  isLocked: boolean;
  isAuthenticated: boolean;
  isInitError: boolean;
  isInitSignUp: boolean;
}

const dueToExpire = (timestamp: Date): boolean => {
  const HOURS_BEFORE_RENEW = 48;
  return differenceInHours(timestamp, new Date()) < HOURS_BEFORE_RENEW;
};

const FINGERPRINT_PARAM_KEY = 'fingerprint';
const USER_ID_PARAM_KEY = 'user_id';

export const getSession = async (): Promise<AuthDataPayload | undefined> => {
  const params = new URLSearchParams(window.location.search);
  const url = new URL(window.location.href);
  const fingerprint = params.get(FINGERPRINT_PARAM_KEY);
  const userId = params.get(USER_ID_PARAM_KEY);

  if (fingerprint && userId) {
    const session = await postSimpleLogin({ userId, fingerprint });

    url.searchParams.delete(FINGERPRINT_PARAM_KEY);
    url.searchParams.delete(USER_ID_PARAM_KEY);
    window.history.replaceState(null, '', url);

    if (session.error) {
      Tracking.captureException(session.error, {
        endpoint: 'getSession.postSimpleLogin',
        params: `${fingerprint}-${userId}`,
      });
      return;
    }

    return session.data;
  }

  const session = await getAuthToken();

  if (session.error) {
    Tracking.captureException(session.error, {
      endpoint: 'getSession.getAuthToken',
      params: `${fingerprint}-${userId}`,
    });
    return;
  }

  if (dueToExpire(new Date(session.data.expiry))) {
    const newSession = await getRefreshToken(session.data.token);

    if (!newSession.error) {
      Tracking.captureException(session.error, {
        endpoint: 'getSession.getRefreshToken',
        params: `${fingerprint}-${userId}`,
      });
      return newSession.data;
    }
  }

  return session.data;
};

export const useAuth = (): UseAuth => {
  const navigate = useNavigate();
  const location = useLocation();
  // const { t } = useTranslation(['news']);

  const { identifyUser } = usePostHog();

  const {
    email,
    resetAuthStore,
    updateAuthStore,
    authProcessing,
    setAuthProcessing,
    unLockAccount,
    isLocked,
    isAuthenticated,
    isInitError,
    isInitSignUp,
  } = useAuthStore(state => ({
    email: state.email,
    unLockAccount: state.unlock,
    resetAuthStore: state.destroy,
    updateAuthStore: state.update,
    isLocked: state.isLocked,
    authProcessing: state.authProcessing,
    setAuthProcessing: state.setAuthProcessing,
    isAuthenticated: !!state.token,
    isInitError: state.isInitError,
    isInitSignUp: state.isInitSignUp,
  }));
  const posthogClient = usePostHogBase();

  // const initializeUser = useUser(state => state.initialize);
  // const initializeRemoteStorage = useRemoteStorage(state => state.initialize);

  // const initializePromoCodeStore = usePromoCodeStore(state => state.initialize);
  // const initWatchlist = useWatchlist(state => state.getLists);
  // const initScreenerFiltersStore = useScreenerStore(state => state.initStore);
  // const initNewsSearchStore = useNewsSearchStore(state => state.initStore);
  const initThemeStore = useThemeStore(state => state.initStore);
  // const initFlagsStore = useFlags(state => state.initialise);
  // const initOnboardingStore = useOnboardingStore(state => state.initStore);

  // const getDocsPendingConsent = useLegalDocuments(
  //   state => state.getDocsPendingConsent
  // );

  const initStores = async (
    session: AuthDataPayload,
    isInitSignUp?: boolean
  ) => {
    identifyUser(session.user_id, session.email, session.lseg_id);

    setAuthProcessing(true);
    updateAuthStore(session, !!isInitSignUp);

    // const responses = await Promise.all([
    //   // initializeRemoteStorage(),
    //   // initializeSubscription(captureUserProperties),
    //   // initializePromoCodeStore(),
    //   getDocsPendingConsent(),
    // ]);
    // await initOnboardingStore(userResponse.data);
    // await initFlagsStore();
    // await initWatchlist();
    // await initScreenerFiltersStore();
    // await initNewsSearchStore(t);
    initThemeStore();

    //const truthyResponses = [...responses].filter(Boolean);

    // const unHandledError = (r: typeof truthyResponses[0]) =>
    //   r?.error && r.error.status !== httpCode.locked;
    // const isResponsesError = truthyResponses.some(unHandledError);

    // if (isResponsesError) {
    //   setIsInitError(true);
    //   navigate(appPaths.error);
    // }

    setAuthProcessing(false);
  };

  const signUp = async (userDetails: SignUpProps) => {
    const registered = await postRegister(userDetails);

    if (registered.error) {
      return registered;
    }

    const session = await postLogin({
      username: userDetails.email,
      password: userDetails.password,
    });

    if (session.error) {
      return session;
    }

    await initStores(session.data, true);
    return session;
  };

  const logout = async (noQuery = false) => {
    resetStores();
    resetAuthStore();
    posthogClient.reset();

    const clearRedirects = [appPaths.base].some(
      path => path === location.pathname
    );

    const queryStr =
      noQuery || clearRedirects
        ? ''
        : queryString({
            [queryKeys.redirect]: `${location.pathname}${location.search}`,
          });

    navigate(authPaths.login + queryStr);
    await postLogout();
  };

  return {
    email,
    isInitError,
    isInitSignUp,
    isAuthenticated,
    isLocked,
    unLockAccount,
    signUp,
    logout,
    initStores,
    authProcessing,
    setAuthProcessing,
  };
};
